import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, X, PaperPlaneTilt } from "@phosphor-icons/react";
import api from "../lib/api";
import "./ChatWidget.css";

/**
 * Scout — PriceGuard's friendly Claude-powered chat widget.
 *
 * Non-intrusive design rules:
 *   - Renders a small floating bubble; the panel itself is only mounted to
 *     the DOM after first open (lazy).
 *   - Never opens itself. No badges, no pop-out tooltips, no notification dots.
 *   - Hidden entirely on authenticated app surfaces (dashboard, vault, etc.) —
 *     once a visitor signs up, marketing chat would just get in the way.
 *   - `Esc` closes; close button + outside content remains scrollable.
 *
 * Backend: POSTs `{messages: [{role, content}]}` to `/api/chat`. The server
 * caps history at 20 messages and IP rate-limits, so we don't trim client-side.
 */

const SUGGESTIONS = [
  "How does price protection work?",
  "Is PriceGuard free?",
  "Which retailers are supported?",
  "What can I store in the vault?",
];

const HIDE_ON_PATHS = [
  "/dashboard",
  "/wishlist",
  "/vault",
  "/purchase/",
  "/admin",
  "/billing/callback",
  "/auth/",
  "/settings",
];

const INITIAL_GREETING = {
  id: "scout-greeting",
  role: "assistant",
  content:
    "Hi, I'm Scout. I can answer questions about wishlists, price protection, receipts and warranties on PriceGuard NZ. What would you like to know?",
};

// Monotonic id for chat messages so React keys stay stable across re-renders
// without relying on array index.
let _msgCounter = 0;
const nextMsgId = () => `msg-${Date.now()}-${++_msgCounter}`;

export default function ChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false); // lazy-mount panel
  const [messages, setMessages] = useState([INITIAL_GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const hidden = HIDE_ON_PATHS.some((p) => pathname.startsWith(p));

  // Scroll to the latest message on every update.
  useEffect(() => {
    if (!open) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending, open]);

  // Focus the input when the panel opens.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 220);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Esc closes the panel.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  const sendMessage = useCallback(async (text) => {
    const cleaned = text.trim();
    if (!cleaned || sending) return;
    const userMsg = { id: nextMsgId(), role: "user", content: cleaned };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput("");
    setSending(true);

    try {
      // Sticky anonymous session id, persisted in localStorage so a returning
      // visitor's transcript stitches together across page loads.
      let sid = null;
      try { sid = window.localStorage.getItem("scout_sid"); } catch { /* private mode */ }
      const { data } = await api.post("/chat", {
        messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
        session_id: sid || undefined,
        page: pathname,
      });
      if (data.session_id && data.session_id !== sid) {
        try { window.localStorage.setItem("scout_sid", data.session_id); } catch { /* no-op */ }
      }
      setMessages((prev) => [...prev, { id: nextMsgId(), role: "assistant", content: data.reply }]);
      try {
        if (window.gtag) window.gtag("event", "scout_reply", { path: pathname });
      } catch { /* no-op */ }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Sorry, Scout is having trouble right now. Please try again in a moment, or email hello@priceguard.nz.";
      setMessages((prev) => [...prev, { id: nextMsgId(), role: "assistant", content: detail }]);
    } finally {
      setSending(false);
    }
  }, [messages, sending, pathname]);

  const toggle = () => {
    if (!hasOpened) setHasOpened(true);
    setOpen((v) => !v);
    try {
      if (!open && window.gtag) window.gtag("event", "scout_open", { path: pathname });
    } catch { /* no-op */ }
  };

  if (hidden) return null;

  return (
    <>
      <button
        type="button"
        className="scout-launcher"
        aria-label={open ? "Close chat" : "Open chat with Scout"}
        aria-expanded={open}
        aria-controls="scout-panel"
        onClick={toggle}
        data-testid="scout-launcher"
      >
        {open ? <X size={22} weight="bold" /> : <ShieldCheck size={26} weight="fill" />}
      </button>

      {hasOpened && (
        <div
          id="scout-panel"
          className="scout-panel"
          role="dialog"
          aria-modal="false"
          aria-label="Scout, PriceGuard Assistant"
          style={{ display: open ? "flex" : "none" }}
          data-testid="scout-panel"
        >
          <div className="scout-header">
            <div className="scout-header-icon"><ShieldCheck size={18} weight="fill" /></div>
            <div className="scout-header-text">
              <h2>Scout</h2>
              <p>by PriceGuard</p>
            </div>
            <div className="scout-online">
              <span>Online</span>
              <div className="scout-online-dot" />
            </div>
            <button type="button" className="scout-close" onClick={() => setOpen(false)} aria-label="Close chat" data-testid="scout-close">
              <X size={18} weight="bold" />
            </button>
          </div>

          <div className="scout-messages" role="log" aria-live="polite" aria-label="Chat messages">
            {messages.map((m) => (
              <div key={m.id} className={`scout-msg ${m.role === "user" ? "user" : "bot"}`}>
                <div className="scout-bubble">{m.content}</div>
                <span className="scout-msg-label">{m.role === "user" ? "You" : "Scout"}</span>
              </div>
            ))}
            {sending && (
              <div className="scout-msg bot">
                <div className="scout-bubble scout-typing" aria-label="Scout is typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {messages.length === 1 && !sending && (
            <div className="scout-suggestions" data-testid="scout-suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="scout-suggestion" onClick={() => sendMessage(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            className="scout-input-row"
            onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
          >
            <input
              ref={inputRef}
              type="text"
              className="scout-input"
              placeholder="Ask Scout anything…"
              aria-label="Type your message"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={2000}
              disabled={sending}
              data-testid="scout-input"
            />
            <button type="submit" className="scout-send" aria-label="Send message" disabled={sending || !input.trim()} data-testid="scout-send">
              <PaperPlaneTilt size={18} weight="fill" />
            </button>
          </form>

          <div className="scout-branding">
            Powered by <a href="https://priceguard.nz/" target="_blank" rel="noopener noreferrer">PriceGuard</a>
          </div>
        </div>
      )}
    </>
  );
}
